// The home notification feed, as both of its views read it: the panel on Home
// and the bell in the header (TGCLOUD-1896). One list (GET
// /clinical-notes/notifications, useNotificationFeed), one window, one way of
// describing an item and one way of opening it, so the two cannot disagree
// about what is outstanding. They used to: each asked the server its own
// question and filtered the answer its own way, and showed 5 and 12 at once.

import { CONSTANTS } from 'src/components/Constants';
import { formatLocalDate } from 'src/i18n/dates';
import { getBucket } from './recordDueDates';

// The one feed item that is not a record (TGCLOUD-1899): how many variant reclassifications the
// reader still has to look at. It opens the Variant Monitoring worklist and cannot be dismissed;
// acknowledging the changes there takes it away.
export const VARIANT_RECLASSIFICATION_TYPE = 'Variant reclassification';

/**
 * `createdBy` as a plain id, whichever shape the record stored it in, for the
 * bell's "My Notifications" switch.
 */
export const normaliseFeedItems = (items) =>
  (Array.isArray(items) ? items : []).map((n) => ({
    ...n,
    createdBy: String(
      (n.createdBy && (n.createdBy._id || n.createdBy)) ||
        n.creatorId ||
        n.creator ||
        n.ownerId ||
        ''
    )
  }));

/**
 * The items the views show: everything the server sent that is due no later
 * than FUTURE_WINDOW_DAYS ahead on the user's own calendar day. The server
 * reads a day further so that no time zone loses an item; this is the cut.
 */
export const feedInWindow = (items, now = new Date()) =>
  (items || []).filter((n) => getBucket(n, now) !== null);

// ─── Describing an item ──────────────────────────────────────────────────────
//
// Each line is one sentence with the record's values as placeholders
// (TGCLOUD-1893): the values are the record's own and are filled in after
// translation, never sent to it. Dates are written in the selected language.

const DATE = { year: 'numeric', month: 'short', day: 'numeric' };
const DATE_TIME = { ...DATE, hour: '2-digit', minute: '2-digit', hour12: true };

export function describeNotification(n, t, lang) {
  const date = (value, opts = DATE) => formatLocalDate(value, lang, opts);
  switch (n.type) {
    case 'Appointment':
      return [
        n.inviteeName ? `${n.inviteeName}` : '',
        n.appointmentDate ? date(n.appointmentDate, DATE_TIME) : '',
        n.clinicName || ''
      ]
        .filter(Boolean)
        .join(' · ');

    case 'Test':
      return [
        n.testName ? t('Test: {{name}}', { name: n.testName }) : t('Test'),
        n.dateResultDue
          ? t('Due {{date}}', { date: date(n.dateResultDue) })
          : '',
        n.lab || ''
      ]
        .filter(Boolean)
        .join(' · ');

    case 'Referral':
      return n.referralTo
        ? t('Referral to {{name}}', { name: n.referralTo })
        : t('Referral');

    case 'Contact':
      return [
        n.contactType || t('Contact'),
        n.contactDate ? date(n.contactDate) : ''
      ]
        .filter(Boolean)
        .join(' · ');

    case 'Clinical Note':
      return [
        t('Clinical note'),
        n.reminderDate || n.clinicalNoteDate
          ? date(n.reminderDate || n.clinicalNoteDate)
          : ''
      ]
        .filter(Boolean)
        .join(' · ');

    case 'Sample':
      return [
        n.sampleType
          ? t('Sample: {{type}}', { type: n.sampleType })
          : t('Sample'),
        n.reminderDate || n.dateTaken
          ? date(n.reminderDate || n.dateTaken)
          : '',
        n.sampleHeldBy ? t('Held by {{name}}', { name: n.sampleHeldBy }) : ''
      ]
        .filter(Boolean)
        .join(' · ');

    case 'Consent':
      return [
        n.consentType
          ? t('Consent: {{type}}', { type: n.consentType })
          : t('Consent'),
        n.reminderDate || n.consentDate
          ? date(n.reminderDate || n.consentDate)
          : '',
        n.response ? t('Response: {{response}}', { response: n.response }) : ''
      ]
        .filter(Boolean)
        .join(' · ');

    case VARIANT_RECLASSIFICATION_TYPE:
      return n.count === 1
        ? t('1 variant reclassification to review')
        : t('{{count}} variant reclassifications to review', {
            count: n.count ?? 0
          });

    default:
      return n.type ? t(n.type) : '';
  }
}

// ─── Opening an item ─────────────────────────────────────────────────────────

// The query parameter the pedigree chart opens each record type's modal from.
const RECORD_PARAM = Object.freeze({
  Appointment: 'appointmentId',
  Test: 'testId',
  Referral: 'referralId',
  Contact: 'contactId',
  'Clinical Note': 'clinicalNoteId',
  Sample: 'sampleId',
  Consent: 'consentId'
});

/**
 * Where an item opens: its person on the pedigree, with its record's modal.
 * Null when the item cannot be placed.
 */
export function recordPathOf(n) {
  if (n?.type === VARIANT_RECLASSIFICATION_TYPE) {
    return CONSTANTS.variantMonitoringPath;
  }
  if (!n?.familyId || !n.familyMemberId || !n.id) return null;
  const param = RECORD_PARAM[n.type];
  return (
    CONSTANTS.pedigreeChartPath.replace(':_id', n.familyId) +
    `?familyMemberId=${n.familyMemberId}` +
    (param ? `&${param}=${n.id}` : '')
  );
}
